/**
 * The durability layer, PLAN.md §7 step 4.
 *
 * Every `MediaRecorder` blob is written to IndexedDB *before* any network
 * attempt, and deleted only once the ingest service acks that seq. A closed
 * laptop, a killed tab or two hours of no wifi therefore cost nothing: the
 * chunks are still on disk when the app comes back.
 *
 * Two stores:
 *   sessions - one row per recording, enough to resume without Convex
 *   chunks   - keyed [recordingId, seq], holding the blob itself
 */

const DB_NAME = "lls-recorder";
const DB_VERSION = 1;
const SESSIONS = "sessions";
const CHUNKS = "chunks";

export interface RecorderSession {
  recordingId: string;
  lessonId: string | null;
  mimeType: string;
  startedAt: number;
  /** Set when the user pressed stop; the uploader still has chunks to drain. */
  stopRequested: boolean;
  /** Set once ingest confirms the concatenated file exists. */
  finalized: boolean;
  /** Next seq the recorder will hand out. */
  nextSeq: number;
  /** Shown in the recovery banner. */
  label?: string;
  /**
   * Set for a dropped file. Its slices are never copied into IndexedDB - the
   * file is the original - so recovery asks for it again and checks it is the
   * same one before resuming.
   */
  upload?: UploadedFileInfo;
}

export interface UploadedFileInfo {
  fileName: string;
  fileBytes: number;
  lastModified: number;
  chunkBytes: number;
}

export interface StoredChunk {
  recordingId: string;
  seq: number;
  clientTs: number;
  blob: Blob;
  bytes: number;
}

let dbPromise: Promise<IDBDatabase> | null = null;

function request<T>(req: IDBRequest<T>): Promise<T> {
  return new Promise((resolve, reject) => {
    req.onsuccess = () => resolve(req.result);
    req.onerror = () => reject(req.error ?? new Error("IndexedDB request failed"));
  });
}

function done(tx: IDBTransaction): Promise<void> {
  return new Promise((resolve, reject) => {
    tx.oncomplete = () => resolve();
    tx.onerror = () => reject(tx.error ?? new Error("IndexedDB transaction failed"));
    tx.onabort = () => reject(tx.error ?? new Error("IndexedDB transaction aborted"));
  });
}

export function openDb(): Promise<IDBDatabase> {
  if (dbPromise !== null) return dbPromise;
  dbPromise = new Promise((resolve, reject) => {
    const open = indexedDB.open(DB_NAME, DB_VERSION);
    open.onupgradeneeded = () => {
      const db = open.result;
      if (!db.objectStoreNames.contains(SESSIONS)) {
        db.createObjectStore(SESSIONS, { keyPath: "recordingId" });
      }
      if (!db.objectStoreNames.contains(CHUNKS)) {
        const chunks = db.createObjectStore(CHUNKS, { keyPath: ["recordingId", "seq"] });
        chunks.createIndex("byRecording", "recordingId", { unique: false });
      }
    };
    open.onsuccess = () => resolve(open.result);
    open.onerror = () => reject(open.error ?? new Error("Could not open IndexedDB"));
  });
  return dbPromise;
}

export async function putSession(session: RecorderSession): Promise<void> {
  const db = await openDb();
  const tx = db.transaction(SESSIONS, "readwrite");
  tx.objectStore(SESSIONS).put(session);
  await done(tx);
}

export async function patchSession(
  recordingId: string,
  patch: Partial<RecorderSession>,
): Promise<void> {
  const db = await openDb();
  const tx = db.transaction(SESSIONS, "readwrite");
  const store = tx.objectStore(SESSIONS);
  const existing = (await request(store.get(recordingId))) as RecorderSession | undefined;
  if (existing !== undefined) store.put({ ...existing, ...patch });
  await done(tx);
}

export async function getSession(recordingId: string): Promise<RecorderSession | null> {
  const db = await openDb();
  const tx = db.transaction(SESSIONS, "readonly");
  const found = (await request(tx.objectStore(SESSIONS).get(recordingId))) as
    | RecorderSession
    | undefined;
  return found ?? null;
}

export async function listSessions(): Promise<RecorderSession[]> {
  const db = await openDb();
  const tx = db.transaction(SESSIONS, "readonly");
  const all = (await request(tx.objectStore(SESSIONS).getAll())) as RecorderSession[];
  return all.sort((a, b) => b.startedAt - a.startedAt);
}

/** The write that makes a chunk safe. Nothing else may run before it. */
export async function putChunk(chunk: StoredChunk): Promise<void> {
  const db = await openDb();
  const tx = db.transaction(CHUNKS, "readwrite");
  tx.objectStore(CHUNKS).put(chunk);
  await done(tx);
}

export async function getChunk(
  recordingId: string,
  seq: number,
): Promise<StoredChunk | null> {
  const db = await openDb();
  const tx = db.transaction(CHUNKS, "readonly");
  const found = (await request(tx.objectStore(CHUNKS).get([recordingId, seq]))) as
    | StoredChunk
    | undefined;
  return found ?? null;
}

/** Called on ack, and only on ack. */
export async function deleteChunk(recordingId: string, seq: number): Promise<void> {
  const db = await openDb();
  const tx = db.transaction(CHUNKS, "readwrite");
  tx.objectStore(CHUNKS).delete([recordingId, seq]);
  await done(tx);
}

export async function pendingSeqs(recordingId: string): Promise<number[]> {
  const db = await openDb();
  const tx = db.transaction(CHUNKS, "readonly");
  const index = tx.objectStore(CHUNKS).index("byRecording");
  const keys = (await request(index.getAllKeys(IDBKeyRange.only(recordingId)))) as
    | IDBValidKey[]
    | undefined;
  return (keys ?? [])
    .map((key) => Number((key as [string, number])[1]))
    .sort((a, b) => a - b);
}

export async function pendingBytes(recordingId: string): Promise<number> {
  const db = await openDb();
  const tx = db.transaction(CHUNKS, "readonly");
  const index = tx.objectStore(CHUNKS).index("byRecording");
  const rows = (await request(index.getAll(IDBKeyRange.only(recordingId)))) as StoredChunk[];
  return rows.reduce((total, row) => total + row.bytes, 0);
}

/** Drops a recording entirely - used by "Discard" in the recovery banner. */
export async function clearRecording(recordingId: string): Promise<void> {
  const db = await openDb();
  const tx = db.transaction([CHUNKS, SESSIONS], "readwrite");
  const index = tx.objectStore(CHUNKS).index("byRecording");
  const keys = (await request(index.getAllKeys(IDBKeyRange.only(recordingId)))) as
    | IDBValidKey[]
    | undefined;
  for (const key of keys ?? []) tx.objectStore(CHUNKS).delete(key);
  tx.objectStore(SESSIONS).delete(recordingId);
  await done(tx);
}
